import React, { useState, useEffect } from 'react';
import {
    styled,
    Box,
    Button,
    Typography,
    Grid,
    Card as _Card,
    CardContent as _CardContent,
    CardActions,
    CircularProgress,
} from '@mui/material';
import { FormControl, FormGroup, FormControlLabel, Checkbox, FormHelperText } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Alert } from '@mui/material';
import { Page, ControlBar, ControlBarHeader } from 'components/ui/layout';
import { ActionButton } from 'components/ui/buttons';
import { useNotification } from 'context/notification';
import ReactGA from 'react-ga';
import { downloadFile } from 'utility/browser';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useAuth } from 'context/auth';
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';

import AssignmentIcon from '@mui/icons-material/Assignment';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import FullScreenModal from 'components/popups/FullScreenModal';
import RegistrationSummary from 'components/RegistrationSummary';

export default function Downloads(props) {
    const { auth } = useAuth();

    const eventId = props.match.params.id;
    const hostId = auth.hostId;

    return (
        <Page>
            <ControlBar>
                <ControlBarHeader>Downloads</ControlBarHeader>
            </ControlBar>

            <Box mb={4}>
                <Alert severity="info">
                    All files are generated in real-time, providing you with the most up-to-date
                    data available.
                </Alert>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <SalesReports eventId={eventId} hostId={hostId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PosSalesReports eventId={eventId} hostId={hostId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CheckinReports eventId={eventId} hostId={hostId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TrackingLinkReport eventId={eventId} hostId={hostId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <GuestList eventId={eventId} hostId={hostId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <EmailExport eventId={eventId} hostId={hostId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CheckoutResponses eventId={eventId} hostId={hostId} />
                </Grid>
            </Grid>
        </Page>
    );
}

function GuestList({ eventId, hostId }) {
    const [loading, setLoading] = useState(false);
    const [format, setFormat] = useState(null); // Shows a confirmation modal when a format is selected

    const { notify } = useNotification();

    async function download(products) {
        const prodFilter = products.toString();

        try {
            if (loading) return;
            setLoading(true);
            ReactGA.event({
                category: 'Orders',
                action: 'Download Guest List',
                label: `${hostId}`,
            });

            const res = await fetchExpresso(
                `/apiv2/events/${eventId}/guest_list?format=${format}&products=${prodFilter}`,
            );

            if (res.status === 200) {
                let file;
                if (format === 'pdf') file = await res.text();
                if (format === 'xlsx') file = await res.blob();
                downloadFile(`Guest-List-${moment().format('YYYY-MM-DD')}.${format}`, file);
            } else if (res.status === 204) {
                notify.warning('No guests found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your guest list');
        }
        setLoading(false);
        setFormat(null);
    }

    return (
        <Card>
            <CardHeader>
                <AssignmentIcon />
                <Title>Guest List</Title>
            </CardHeader>
            <CardContent>
                <Typography>
                    Download a comprehensive list of attendees and their ticket purchases. Perfect
                    for creating printed guest lists at your event's entry point. Alternatively, use
                    the <Bold>FrontDoor+ | Guest Check-in</Bold> app for smooth attendee management.
                </Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={() => setFormat('pdf')} startIcon={<GetAppIcon />}>
                    PDF
                </ActionButton>
                <ActionButton onClick={() => setFormat('xlsx')} startIcon={<GetAppIcon />}>
                    EXCEL
                </ActionButton>
            </CardActions>

            {format !== null && (
                <ProductPicker
                    title="Guest List"
                    description="Include the following tickets:"
                    loading={loading}
                    eventId={eventId}
                    onCancel={() => setFormat(null)}
                    onConfirm={download}
                />
            )}
        </Card>
    );
}

function EmailExport({ eventId, hostId }) {
    const [loading, setLoading] = useState(false);
    const [format, setFormat] = useState(null);

    const { notify } = useNotification();

    async function download(products) {
        const prodFilter = products.toString();

        if (loading) return;
        setLoading(true);

        ReactGA.event({ category: 'Orders', action: 'Download Email Export', label: `${hostId}` });

        try {
            const res = await fetchExpresso(
                `/apiv2/events/${eventId}/email_list?format=${format}&products=${prodFilter}`,
            );

            if (res.status === 200) {
                const file = await res.blob();
                downloadFile(`Mailing-List-${moment().format('YYYY-MM-DD')}.${format}`, file);
            } else if (res.status === 204) {
                notify.warning('No emails found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your mailing list');
        }
        setLoading(false);
        setFormat(null);
    }

    return (
        <Card>
            <CardHeader>
                <AlternateEmailIcon />
                <Title>Mailing List</Title>
            </CardHeader>
            <CardContent>
                <Typography>
                    Download your event attendees' names and email addresses in a format optimized
                    for easy import into popular platforms like Mailchimp (newsletters) and Hubspot
                    (crm), enhancing your marketing campaigns.
                </Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={() => setFormat('csv')} startIcon={<GetAppIcon />}>
                    CSV
                </ActionButton>
                <ActionButton onClick={() => setFormat('xlsx')} startIcon={<GetAppIcon />}>
                    EXCEL
                </ActionButton>
            </CardActions>

            {format !== null && (
                <ProductPicker
                    title="Mailing List"
                    description="Include people who bought the following tickets:"
                    loading={loading}
                    eventId={eventId}
                    onCancel={() => setFormat(null)}
                    onConfirm={download}
                />
            )}
        </Card>
    );
}

function SalesReports({ eventId, hostId }) {
    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    async function download() {
        try {
            if (loading) return;
            setLoading(true);
            ReactGA.event({ category: 'Orders', action: 'Download Orders', label: `${hostId}` });

            const res = await fetchExpresso(`/apiv2/events/${eventId}/order_list`);

            if (res.status === 200) {
                const excel = await res.blob();
                downloadFile(
                    `Online-Sales-Report-${moment().format('YYYY-MM-DD-HHmm')}.xlsx`,
                    excel,
                );
            } else if (res.status === 204) {
                notify.warning('No sales found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your report');
        }
        setLoading(false);
    }

    return (
        <Card>
            <CardHeader>
                <ReceiptIcon />
                <Title>Online Sales Reports</Title>
            </CardHeader>
            <CardContent>
                <Typography>Download detailed sales and order reports.</Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={download} startIcon={<GetAppIcon />}>
                    Excel
                </ActionButton>
            </CardActions>
        </Card>
    );
}

function PosSalesReports({ eventId, hostId }) {
    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    async function download() {
        try {
            if (loading) return;
            setLoading(true);
            ReactGA.event({
                category: 'Orders',
                action: 'Download POS Orders',
                label: `${hostId}`,
            });

            const res = await fetchExpresso(`/apiv2/events/${eventId}/pos_order_list`);

            if (res.status === 200) {
                const excel = await res.blob();
                downloadFile(
                    `In-Person-Sales-Report-${moment().format('YYYY-MM-DD-HHmm')}.xlsx`,
                    excel,
                );
            } else if (res.status === 204) {
                notify.warning('No sales found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your report');
        }
        setLoading(false);
    }

    return (
        <Card>
            <CardHeader>
                <ReceiptIcon />
                <Title>In-Person Sales Reports</Title>
            </CardHeader>
            <CardContent>
                <Typography>Download a detailed report on sales you made in-person.</Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={download} startIcon={<GetAppIcon />}>
                    Excel
                </ActionButton>
            </CardActions>
        </Card>
    );
}

function CheckinReports({ eventId, hostId }) {
    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    async function download() {
        try {
            if (loading) return;
            setLoading(true);
            ReactGA.event({
                category: 'Orders',
                action: 'Download Check-in Report',
                label: `${hostId}`,
            });

            const res = await fetchExpresso(`/apiv2/events/${eventId}/scan_report`);

            if (res.status === 200) {
                const excel = await res.blob();
                downloadFile(`Check-In-Reports-${moment().format('YYYY-MM-DD')}.xlsx`, excel);
            } else if (res.status === 204) {
                notify.warning('No check-ins found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your report');
        }
        setLoading(false);
    }

    return (
        <Card>
            <CardHeader>
                <EqualizerIcon />
                <Title>Check-in Reports</Title>
            </CardHeader>
            <CardContent>
                <Typography>
                    Download reports detailing attendee check-in activity to help you evaluate the
                    success of your entry processes.
                </Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={download} startIcon={<GetAppIcon />}>
                    Excel
                </ActionButton>
            </CardActions>
        </Card>
    );
}

function CheckoutResponses({ eventId, hostId }) {
    const { notify } = useNotification();

    // *** Response Summary *** //

    const [summaryOpen, setSummaryOpen] = useState(false);

    const openSummary = () => setSummaryOpen(true);
    const closeSummary = () => setSummaryOpen(false);

    // *** Response Report Download *** //

    const [loadingReport, setLoadingReport] = useState(false);

    async function downloadReport() {
        try {
            if (loadingReport) return;
            setLoadingReport(true);
            ReactGA.event({
                category: 'Orders',
                action: 'Download Checkout Question Report',
                label: `${hostId}`,
            });

            const res = await fetchExpresso(`/apiv2/events/${eventId}/registration/report`);

            if (res.status === 200) {
                const excel = await res.blob();
                downloadFile(`Checkout-Responses-${moment().format('YYYY-MM-DD')}.xlsx`, excel);
            } else if (res.status === 204) {
                notify.warning('No checkout responses found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your report');
        }
        setLoadingReport(false);
    }

    return (
        <Card>
            <CardHeader>
                <EqualizerIcon />
                <Title>Checkout Responses</Title>
            </CardHeader>
            <CardContent>
                <Typography>
                    See a summary of all responses to your checkout questions, or download every
                    response in an excel sheet.
                </Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={openSummary} startIcon={<VisibilityIcon />}>
                    Summary
                </ActionButton>
                <ActionButton onClick={downloadReport} startIcon={<GetAppIcon />}>
                    Excel
                </ActionButton>
            </CardActions>

            <FullScreenModal open={summaryOpen} onClose={closeSummary} title="Checkout Responses">
                {summaryOpen && <RegistrationSummary eventId={eventId} onClose={closeSummary} />}
            </FullScreenModal>
        </Card>
    );
}

function ProductPicker(props) {
    const { onCancel, onConfirm, title, description, loading, eventId } = props;

    const [state, setState] = useState({
        status: 'loading',
        checklist: null,
    });

    const [formError, setFormError] = useState(null);

    useEffect(() => {
        fetchExpresso(`/apiv2/products/?event=${eventId}`)
            .then(async (res) => {
                if (res.status === 200) {
                    const products = await res.json();
                    const checklist = products.map((p) => ({
                        id: p.product_id,
                        name: p.prod_name,
                        checked: true,
                    }));
                    setState({ status: 'success', checklist: checklist });
                }
            })
            .catch(() => {
                setState({ status: 'error', products: null });
            });
    }, [eventId]);

    const handleConfirm = () => {
        const checkedProducts = state.checklist.filter((p) => p.checked === true).map((p) => p.id);

        if (checkedProducts.length > 0) {
            onConfirm(checkedProducts);
        } else {
            setFormError('Please select at least one ticket');
        }
    };

    const handleCheckboxToggle = (id) => {
        if (formError) setFormError(null);

        const newChecklist = state.checklist.map((p) => {
            if (p.id === id) {
                return { ...p, checked: !p.checked };
            } else {
                return p;
            }
        });
        setState((s) => ({ ...s, checklist: newChecklist }));
    };

    return (
        <Dialog open fullWidth onClose={onCancel}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {state.status === 'loading' && (
                    <Box
                        display="flex"
                        width="100%"
                        height={120}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <CircularProgress />
                    </Box>
                )}
                {state.status === 'error' && (
                    <Box
                        display="flex"
                        width="100%"
                        height={120}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography>There was a problem loading your tickets</Typography>
                    </Box>
                )}
                {state.status === 'success' && state.checklist.length === 0 && (
                    <Box
                        display="flex"
                        width="100%"
                        height={120}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography>No tickets found</Typography>
                    </Box>
                )}
                {state.status === 'success' && state.checklist.length > 0 && (
                    <Box style={{ minWidth: '60%' }}>
                        <Typography>{description}</Typography>

                        <FormControl error={Boolean(formError)}>
                            <FormGroup>
                                {state.checklist.map((p) => (
                                    <FormControlLabel
                                        key={p.id}
                                        control={
                                            <Checkbox
                                                checked={p.checked}
                                                onChange={() => handleCheckboxToggle(p.id)}
                                            />
                                        }
                                        label={p.name}
                                    />
                                ))}
                            </FormGroup>
                            {formError && <FormHelperText>{formError}</FormHelperText>}
                        </FormControl>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" disabled={loading} onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    color="secondary"
                    disabled={loading}
                    onClick={handleConfirm}
                    startIcon={<GetAppIcon />}
                >
                    {loading ? <CircularProgress size={16} /> : 'Download'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function TrackingLinkReport({ eventId, hostId }) {
    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    async function download() {
        try {
            if (loading) return;
            setLoading(true);
            ReactGA.event({
                category: 'Orders',
                action: 'Download Tracking Report',
                label: `${hostId}`,
            });

            const res = await fetchExpresso(`/apiv2/events/${eventId}/tracking_link_report`);

            if (res.status === 200) {
                const excel = await res.blob();
                downloadFile(`Tracking-Link-Report-${moment().format('YYYY-MM-DD')}.xlsx`, excel);
            } else if (res.status === 204) {
                notify.warning('No links found');
            } else {
                throw new Error();
            }
        } catch (e) {
            notify.error(e.errorMsg || 'There was a problem downloading your report');
        }
        setLoading(false);
    }

    return (
        <Card>
            <CardHeader>
                <LinkIcon />
                <Title>Tracking Link Report</Title>
            </CardHeader>
            <CardContent>
                <Typography>
                    Download a report on the number of clicks and purchases generated by your
                    tracking links.
                </Typography>
            </CardContent>
            <CardActions>
                <ActionButton onClick={download} startIcon={<GetAppIcon />}>
                    Excel
                </ActionButton>
            </CardActions>
        </Card>
    );
}

const Card = styled(_Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
}));

const CardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
}));

const Title = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
}));
Title.defaultProps = { variant: 'h6' };

const CardContent = styled(_CardContent)(({ theme }) => ({
    flex: 1,
}));

const Bold = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    display: 'inline',
}));
